/**
 * Tag drawing tool (`simpleTag`).
 *
 * Same geometry as klinecharts' built-in template (a horizontal dashed line
 * across the pane at the anchor price, plus a price label on the y-axis),
 * re-registered under the SAME name with interactive figures. The built-in
 * marks its line `ignoreEvent: true` and draws no default point figure, so it
 * has zero hit area: nothing can select, drag or double-click it. Here the line
 * takes events and the anchor handle exists, so the overlay callbacks fire.
 *
 * The y-axis label is drawn by the template itself (`needDefaultYAxisFigure`
 * is false), so klinecharts' default axis pill does not duplicate it.
 *
 * Label contract (as upstream): `overlay.extendData` as a string or number, or
 * a function called with the overlay. When that yields nothing, the anchor
 * price is shown at the pane's price precision.
 */
import { registerOverlay } from 'klinecharts'
import { isNum, lineFigure, type Coord, type OverlayFigure } from './overlayUtils'
import { registerOnce, type OverlayTemplateParam } from './registrationUtils'

export interface TagLineStyle {
  color?: string
  size?: number
}

/** The overlay fields the label reads. */
export interface TagOverlayLike {
  extendData?: unknown
  points?: Array<{ value?: unknown }>
}

export interface TagAxisOptions {
  /** Pane width in px; the label sits at its right edge (or left edge at zero). */
  width: number
  /** Whether the y-axis is anchored at zero (`yAxis.isFromZero()`). */
  isFromZero: boolean
  overlay: TagOverlayLike
  /** Decimal places for the price fallback (`precision.price`). */
  pricePrecision: number
}

/**
 * Horizontal line across the pane at the anchor y. Draggable. The dash is NOT
 * set on the figure: it resolves from the overlay's styles (the template's
 * dashed default, or the user's solid/dashed choice), as upstream did, so the
 * figure never overrides the drawer's line style or klinecharts' dash pattern.
 */
export function buildTagLineFigures(
  coordinates: Coord[],
  width: number,
  line: TagLineStyle = {},
): OverlayFigure[] {
  const anchor = coordinates[0]
  if (!anchor || !isNum(anchor.x) || !isNum(anchor.y) || !isNum(width)) return []
  return [
    lineFigure(
      { x: 0, y: anchor.y },
      { x: width, y: anchor.y },
      { color: line.color, size: line.size },
      true,
    ),
  ]
}

/** Resolve the label text: extendData (string/number, or function result), else the price. */
function resolveTagText(overlay: TagOverlayLike, pricePrecision: number): string {
  const data = overlay.extendData
  const text = typeof data === 'function' ? (data as (o: unknown) => unknown)(overlay) : data
  if (text !== undefined && text !== null) return String(text)
  const price = overlay.points?.[0]?.value
  return isNum(price) ? price.toFixed(pricePrecision) : ''
}

/** Y-axis price label at the anchor y, left-aligned at zero or right-aligned at the pane edge. */
export function buildTagAxisFigures(coordinates: Coord[], opts: TagAxisOptions): OverlayFigure[] {
  const anchor = coordinates[0]
  if (!anchor || !isNum(anchor.x) || !isNum(anchor.y) || !isNum(opts.width)) return []
  return [
    {
      type: 'text',
      attrs: {
        x: opts.isFromZero ? 0 : opts.width,
        y: anchor.y,
        text: resolveTagText(opts.overlay, opts.pricePrecision),
        align: opts.isFromZero ? 'left' : 'right',
        baseline: 'middle',
      },
    },
  ]
}

/** Register the interactive tag template. Idempotent. */
export const registerTagOverlay = registerOnce(() => {
  registerOverlay({
    name: 'simpleTag',
    totalStep: 2, // single click
    needDefaultPointFigure: true,
    needDefaultXAxisFigure: false,
    needDefaultYAxisFigure: false,
    styles: { line: { style: 'dashed' } },
    createPointFigures: ({ coordinates, bounding, overlay }) => {
      const styles = (overlay as { styles?: { line?: TagLineStyle } } | undefined)?.styles
      return buildTagLineFigures(coordinates as Coord[], bounding.width, styles?.line ?? {})
    },
    createYAxisFigures: ({ coordinates, bounding, overlay, yAxis, precision }) =>
      buildTagAxisFigures(coordinates as Coord[], {
        width: bounding.width,
        isFromZero: yAxis?.isFromZero() ?? false,
        overlay: overlay as unknown as TagOverlayLike,
        pricePrecision: precision.price,
      }),
  } as OverlayTemplateParam)
})
